import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { lookupQuoteRequest, isNoMatch } from "../services/quoteService";
import { friendlyError } from "../api/api";
import { formatBusinessDateTime } from "../utils/businessDate";

export default function QuoteStatus() {
  const prefill = useLocation().state || {};
  const [form, setForm] = useState({
    qtnCode: prefill.qtnCode || "",
    phone: prefill.phone || "",
    email: prefill.email || "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | ok | notfound | error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.qtnCode.trim() || (!form.phone.trim() && !form.email.trim())) {
      setStatus("error");
      setErrorMessage("Enter your quote number and the phone or email you used.");
      return;
    }
    setStatus("loading");
    try {
      setResult(await lookupQuoteRequest(form));
      setStatus("ok");
    } catch (err) {
      if (isNoMatch(err)) {
        setStatus("notfound");
      } else if (err?.response?.status === 404) {
        setStatus("error");
        setErrorMessage("Quote lookup is not available right now. Please try again later.");
      } else {
        setStatus("error");
        setErrorMessage(friendlyError(err, "Unable to check your request. Please try again."));
      }
    }
  };

  const inputClass =
    "w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300";

  return (
    <div className="max-w-md mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-neutral-900 mb-2">Check a Quote Request</h1>
      <p className="text-sm text-neutral-500 mb-6">
        Enter your quote number and the phone or email you gave when you submitted it.
      </p>

      <form onSubmit={submit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="qs-code" className="block text-sm font-medium text-neutral-700 mb-1">Quote number</label>
          <input
            id="qs-code"
            className={inputClass}
            value={form.qtnCode}
            onChange={(e) => setForm({ ...form, qtnCode: e.target.value })}
            placeholder="QTN-0001"
          />
        </div>
        <div>
          <label htmlFor="qs-phone" className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
          <input
            id="qs-phone"
            className={inputClass}
            value={form.phone}
            autoComplete="tel"
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="qs-email" className="block text-sm font-medium text-neutral-700 mb-1">
            Email (instead of phone)
          </label>
          <input
            id="qs-email"
            type="email"
            className={inputClass}
            value={form.email}
            autoComplete="email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className="w-full bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-3 hover:bg-neutral-800 disabled:opacity-50"
        >
          {status === "loading" ? "Checking..." : "Check status"}
        </button>
      </form>

      <div className="mt-6" aria-live="polite">
        {status === "notfound" && (
          <p className="text-sm text-red-600">
            No matching request found. Check the quote number and the phone or email you used.
          </p>
        )}
        {status === "error" && <p className="text-sm text-red-600">{errorMessage}</p>}
        {status === "ok" && result && (
          <div className="rounded-xl border border-neutral-200 bg-white p-4 text-sm text-neutral-700">
            <p>
              <span className="text-neutral-500">Quote number:</span> <strong>{result.qtn_code}</strong>
            </p>
            <p>
              <span className="text-neutral-500">Status:</span> {result.status}
            </p>
            <p>
              <span className="text-neutral-500">Submitted:</span> {formatBusinessDateTime(result.created_at)}
            </p>
            <ul className="mt-3 space-y-1">
              {result.items.map((i, idx) => (
                <li key={idx} className="flex justify-between">
                  <span>
                    {i.product_name}
                    {i.unit_code ? ` · ${i.unit_code}` : ""}
                  </span>
                  <span className="text-neutral-500">× {i.qty_ordered}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-neutral-400">Prices are provided by the business when they follow up.</p>
          </div>
        )}
      </div>

      <div className="text-center mt-8">
        <Link to="/shop" className="text-sm text-neutral-500 underline">
          Continue Shopping
        </Link>
      </div>
    </div>
  );
}
